import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AppLayout, Header, showToast } from '@ideoz/ui';
import { useMyProfile } from '../modules/profile/useMyProfile';
import { IntegrationsModule, getIntegration } from '../modules/integrations';

/**
 * Account-level Integrations page (`/integrations`). Integrations are user-scoped
 * (not per-startup), so they live under the account sidebar (My Startups / Profile
 * / Billing / Integrations / App Settings) and the user menu — not the startup
 * shell. Reuses the account shell from `useMyProfile`, with Integrations active.
 *
 * The OAuth callback redirects the browser back here with `?connect=success|error`
 * (and `provider`); we toast the outcome, refresh the connected list, and strip
 * the params. Because the flow is a same-tab redirect, the founder always lands
 * back on Ideoz — even when the connection fails.
 */
export default function IntegrationsPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, facade } = useMyProfile('integrations');
  const {
    header: { headerProps },
    subscriptionExpiration: { isExpiringWithin3Days, daysUntilExpiration },
    onRenewSubscription,
  } = facade;

  // Handle the OAuth callback outcome once, then clean the URL.
  const handledCallbackRef = useRef(false);
  useEffect(() => {
    const status = searchParams.get('connect');
    if (!status || handledCallbackRef.current) return;
    handledCallbackRef.current = true;

    const provider = searchParams.get('provider') ?? undefined;
    // Name the tool when we recognize the provider; otherwise fall back to a
    // generic message (the backend may send a provider not in the FE catalog).
    const name = getIntegration(provider)?.name;

    void queryClient.invalidateQueries({ queryKey: ['user-integrations'] });
    if (status === 'success') {
      showToast({
        message: name
          ? t('integrations.toast.connected', { name })
          : t('integrations.toast.connectedGeneric'),
        severity: 'success',
      });
    } else {
      showToast({
        message: name
          ? t('integrations.toast.connectFailedCallback', { name })
          : t('integrations.toast.connectFailedGeneric'),
        severity: 'error',
      });
    }

    // Strip ?connect / ?provider so a refresh doesn't re-toast.
    const next = new URLSearchParams(searchParams);
    next.delete('connect');
    next.delete('provider');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, queryClient, t]);

  return (
    <AppLayout
      currentPath="/integrations"
      sidebar={facade.sidebar}
      user={{ isLoggedin: !!user, name: user?.name, avatar: user?.avatar }}
      userMenuProps={
        user
          ? {
              points: headerProps.points,
              pointsLabel: t('profile.pointsLabel'),
              menuItems: facade.header.userMenuItems || [],
              socialLinks: facade.header.userMenuSocialLinks || [],
              onRequestUpgrade: facade.header.onRequestUpgrade,
              isRequestingUpgrade: facade.header.isRequestingUpgrade || false,
              canRequestUpgrade: (headerProps.points || 0) <= 0,
              isWeb: true,
            }
          : undefined
      }
      subscriptionExpirationBanner={
        isExpiringWithin3Days
          ? { isVisible: true, daysUntilExpiration, onRenew: onRenewSubscription }
          : undefined
      }
      isWeb
      testId="integrations-sidemenu"
      height="screen"
      defaultSidebarCollapsed={false}
    >
      <Header
        {...headerProps}
        showBackButton
        showBackArrow={false}
        backText={t('nav.integrations')}
        isHasBorder
        className="sticky top-0 w-full"
        isWeb
      />

      <IntegrationsModule />
    </AppLayout>
  );
}
